/**
 * Stepper Component
 * Multi-step progress indicator for wizards and forms
 */

import React from 'react'
import { Icon } from '../../atoms/Icon'
import type { StepperProps } from './Stepper.types'
import {
  StepperContainer,
  StepItem,
  StepIconWrapper,
  StepTitle,
  StepConnector,
  ProgressBar,
  ProgressBarFill,
} from './Stepper.styles'

export const Stepper: React.FC<StepperProps> = ({
  steps,
  currentStepIndex,
  onStepClick,
  className,
}) => {
  const handleStepClick = (stepIndex: number) => {
    // Allow clicking on any step if onStepClick is provided
    if (onStepClick) {
      onStepClick(stepIndex)
    }
  }

  const progress = ((currentStepIndex + 1) / steps.length) * 100

  return (
    <>
      <StepperContainer className={className}>
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <StepItem
              $status={step.status}
              $clickable={!!onStepClick}
              onClick={() => handleStepClick(index)}
              role="button"
              tabIndex={onStepClick ? 0 : -1}
              aria-current={step.status === 'current' ? 'step' : undefined}
              aria-label={`${step.title} - ${step.status}`}
            >
              <StepIconWrapper $status={step.status}>
                <Icon
                  name={step.status === 'completed' ? 'check' : step.icon}
                  size="small"
                />
              </StepIconWrapper>
              <StepTitle $status={step.status}>{step.title}</StepTitle>
            </StepItem>

            {/* Connector line between steps */}
            {index < steps.length - 1 && (
              <StepConnector $completed={step.status === 'completed'} />
            )}
          </React.Fragment>
        ))}
      </StepperContainer>

      {/* Progress bar for mobile */}
      <ProgressBar>
        <ProgressBarFill $progress={progress} />
      </ProgressBar>
    </>
  )
}

Stepper.displayName = 'Stepper'
